import { useEffect, useState, useCallback } from "react";
import { Download, KeyRound, Loader2, Users } from "lucide-react";
import { Reveal, Label } from "../components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "nxtplate-admin-key";

export default function WaitlistAdmin() {
  const [key, setKey] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [input, setInput] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [items, setItems] = useState([]);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const load = useCallback(async (k) => {
    setState("loading");
    setError("");
    try {
      const res = await fetch(`${API}/waitlist/list`, { headers: { "X-Admin-Key": k } });
      if (res.status === 401) {
        sessionStorage.removeItem(STORAGE_KEY);
        setKey("");
        setState("idle");
        setError("That admin key isn't right. Check it and try again.");
        return;
      }
      if (!res.ok) throw new Error();
      const data = await res.json();
      setItems(data.items || []);
      setState("ready");
    } catch {
      setState("idle");
      setError("Couldn't reach the server. Try again in a moment.");
    }
  }, []);

  useEffect(() => {
    if (key) load(key);
  }, [key, load]);

  const submit = (e) => {
    e.preventDefault();
    const k = input.trim();
    if (!k) return;
    sessionStorage.setItem(STORAGE_KEY, k);
    setKey(k);
  };

  const download = async () => {
    const res = await fetch(`${API}/waitlist/export.csv`, { headers: { "X-Admin-Key": key } });
    if (!res.ok) {
      setError("Couldn't download the list. Try again.");
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "nxtplate-waitlist.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <main className="min-h-[70vh] bg-cream pt-32 pb-24 md:pt-40">
      <div className="mx-auto max-w-3xl px-6 md:px-10">
        <Reveal><Label>Private</Label></Reveal>
        <Reveal delay={0.08}>
          <h1 className="mt-6 font-display text-4xl font-medium tracking-tight text-neutral-900 md:text-5xl">
            Waitlist <span className="italic text-coral">signups</span>
          </h1>
        </Reveal>

        {state !== "ready" && (
          <Reveal delay={0.16}>
            <form
              onSubmit={submit}
              data-testid="waitlist-admin-key-form"
              className="mt-10 rounded-3xl border border-neutral-200/70 bg-white p-8 shadow-[0_8px_24px_rgba(0,0,0,0.06)]"
            >
              <label htmlFor="admin-key" className="flex items-center gap-2 text-sm font-semibold text-neutral-900">
                <KeyRound className="h-4 w-4 text-coral" aria-hidden="true" />
                Admin key
              </label>
              <p className="mt-2 text-sm text-neutral-500">
                Enter your admin key to view everyone who joined the waitlist.
              </p>
              <div className="mt-5 flex items-center gap-2 rounded-full border border-neutral-200 bg-cream p-1.5">
                <input
                  id="admin-key"
                  type="password"
                  autoComplete="off"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Paste your admin key"
                  data-testid="waitlist-admin-key-input"
                  className="w-full bg-transparent px-4 py-2.5 text-sm text-neutral-900 outline-none placeholder:text-neutral-400"
                />
                <button
                  type="submit"
                  data-testid="waitlist-admin-submit"
                  disabled={state === "loading"}
                  className="inline-flex shrink-0 items-center gap-2 rounded-full bg-coral px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-coral-dark disabled:opacity-70"
                >
                  {state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : "View list"}
                </button>
              </div>
              {error && (
                <p data-testid="waitlist-admin-error" className="mt-3 px-2 text-sm text-coral-dark">{error}</p>
              )}
            </form>
          </Reveal>
        )}

        {state === "ready" && (
          <Reveal>
            <div data-testid="waitlist-admin-panel" className="mt-10">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-neutral-900">
                  <Users className="h-4 w-4 text-teal" aria-hidden="true" />
                  <span data-testid="waitlist-admin-count">{items.length} signup{items.length === 1 ? "" : "s"}</span>
                </p>
                <button
                  onClick={download}
                  data-testid="waitlist-admin-download-button"
                  className="inline-flex items-center gap-2 rounded-full bg-neutral-900 px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:scale-[1.03] hover:shadow-[0_8px_20px_rgba(0,0,0,0.2)]"
                >
                  <Download className="h-4 w-4" aria-hidden="true" />
                  Download CSV
                </button>
              </div>

              <div className="mt-6 overflow-hidden rounded-3xl border border-neutral-200/70 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                {items.length === 0 ? (
                  <p className="p-8 text-sm text-neutral-500">No signups yet — share the site and they'll land here.</p>
                ) : (
                  <table data-testid="waitlist-admin-table" className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-neutral-100 text-xs uppercase tracking-[0.14em] text-neutral-400">
                        <th className="px-6 py-4 font-semibold">Email</th>
                        <th className="hidden px-6 py-4 font-semibold sm:table-cell">Source</th>
                        <th className="hidden px-6 py-4 font-semibold md:table-cell">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr key={`${item.email}-${item.created_at}`} className="border-b border-neutral-50 last:border-0">
                          <td className="px-6 py-4 font-medium text-neutral-900">{item.email}</td>
                          <td className="hidden px-6 py-4 text-neutral-500 sm:table-cell">{item.source || "—"}</td>
                          <td className="hidden px-6 py-4 text-neutral-500 md:table-cell">{fmt(item.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </main>
  );
}
